import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import intranetApi from '../../services/intranetApi';
import RichTextEditor from '../common/RichTextEditor';
import { renderContentWithMentions } from '../../utils/mentions';

const EditComment = ({ isOpen, onClose, onCommentUpdated, comment }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  // Initialize content when comment changes
  useEffect(() => {
    if (comment && isOpen) {
      setContent(comment.content || '');
    }
  }, [comment, isOpen]);

  const handleInputChange = (e) => {
    setContent(e.target.value);
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const validateForm = () => {
    if (!content.trim()) {
      setError('Comment content is required');
      return false;
    }
    
    if (content.length > 2000) {
      setError('Comment must be less than 2,000 characters');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await intranetApi.comments.updateComment(comment.id, {
        content: content.trim()
      });
      
      if (response.data.success) {
        toast.success('Comment updated successfully!');
        onCommentUpdated(response.data.comment);
        handleClose();
      } else {
        throw new Error(response.data.error || 'Failed to update comment');
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error(error.response?.data?.error || 'Failed to update comment');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setPreviewMode(false);
    onClose();
  };

  if (!isOpen || !comment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <ChatBubbleLeftRightIcon className="h-6 w-6 text-indigo-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Edit Comment</h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => setPreviewMode(!previewMode)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 ${
                previewMode 
                  ? 'bg-indigo-100 text-indigo-700' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <EyeIcon className="h-4 w-4 mr-1.5 inline" />
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          {previewMode ? (
            /* Preview Mode */
            <div className="p-6">
              <div className="bg-gray-100 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">
                      {comment.author_name || 'Unknown User'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      Just now (preview)
                    </span>
                  </div>
                </div>
                <div className="text-sm text-gray-700">
                  {content ? renderContentWithMentions(content) : (
                    <span className="text-gray-400 italic">Comment content will appear here...</span>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <form onSubmit={handleSubmit} className="p-6">
              <div>
                <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
                  Comment Content *
                </label>
                <RichTextEditor
                  value={content}
                  onChange={setContent}
                  placeholder="Write your comment here... You can use rich formatting and mention users with @username"
                  className="mb-2"
                  maxLength={2000}
                  error={error}
                />
                {error && (
                  <p className="mt-1 text-sm text-red-600">{error}</p>
                )}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            {previewMode ? (
              'Preview mode - switch back to edit to make changes'
            ) : (
              'Update your comment content above'
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || previewMode}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 flex items-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                <>
                  <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                  Update Comment
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditComment; 